import React, { useEffect, useState } from 'react';

import numberFormatter from '../../../common/numberFormatter';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  TopCompanyJudgmentsDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeState,
  TopCompaniesReportShell,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';
import { CompanyRankingList } from './SuperAdminCompanyRankingList';

export const SuperAdminTopCompaniesJudgmentsTab: React.FC = () => {
  const spd = useSpdCore();

  const [filters, setFilters] = useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [judgments, setJudgments] = useState<TopCompanyJudgmentsDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  const loadData = async (nextFilters: DateRangeState) => {
    setLoading(true);
    try {
      const result = await spd.reports.reportTopCompaniesByJudgments(
        toDateStart(nextFilters.dateFrom),
        toDateEnd(nextFilters.dateTo),
      );
      setJudgments(result);
      setAppliedFilters(nextFilters);
    } catch (error) {
      console.error('Error loading top companies by judgments', error);
      setJudgments([]);
      setErrors(await responseErrors(error as ResponseError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spd.reports]);

  let judgmentsTotal = 0;
  for (const row of judgments) {
    judgmentsTotal += row.judgmentsCount || 0;
  }

  return (
    <>
      <TopCompaniesReportShell
        title="Największe firmy wg liczby orzeczeń"
        totalText={`${numberFormatter(judgmentsTotal)} orzeczeń`}
        totalClassName="text-primary"
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          void loadData(filters);
        }}
        onClear={() => {
          const emptyFilters = { dateFrom: '', dateTo: '' };
          setFilters(emptyFilters);
          void loadData(emptyFilters);
        }}
        spinnerVariant="primary"
      >
        <CompanyRankingList
          items={judgments}
          emptyText="Brak danych w wybranym zakresie."
          cardClassName="top-company-card top-company-card--judgments"
          badgeClassName="company-ranking-badge company-ranking-badge--judgments"
          badgeBg="primary"
          getBadgeText={item => numberFormatter(item.judgmentsCount || 0)}
          getMetaText={item => {
            const count = item.judgmentsCount || 0;
            return `${numberFormatter(count)} ${count === 1 ? 'orzeczenie' : count < 5 ? 'orzeczenia' : 'orzeczeń'}`;
          }}
        />
        <ValidationAlert
          show={errors.length > 0}
          errors={errors}
          className="mt-3"
        />
      </TopCompaniesReportShell>
    </>
  );
};
